import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import ShoppingCart from "../ShoppingCart/ShoppingCart";
import { useLocalStorage } from "../hooks/useLocalStorage";
import React from "react";
import { getPageLocaleUrl, pf } from "../utilities/helpers";

declare var API: any;

type ShoppingCartProviderProps = {
  children: ReactNode;
};

type CartItem = {
  id: number;
  quantity: number;
  addVariationProductProductSelectedVariationData?: any;
  eventId?: number;
  start?: string;
};

type ProductCategoryLocaleTranslations = {
  [locale: string]: {
    name: string;
  };
};

export interface ProductCategory {
  id: number;
  name: string;
  state_id: number;
  display_color: string;
  locale_translations: ProductCategoryLocaleTranslations;
  order_by: number;
  web_state_id: number;
  cashregisterChildren: ProductCategory[];
}

export interface LocationProductDiscount {
  id: number;
  name: string;
  display_name: string;
  value: string;
  accumulates: number;
  starts_at: string;
  ends_at: string;
  indefinite: number;
}

export interface LocationProduct {
  translations: any;
  id: number;
  shortDescription: string;
  posProductCode: string;
  discounts: any;
  adjustments: any;
  price_net_with_discounts: string;
  product: any;
  deliveryTime: any;
  deliveryTimeWithOrdering: any;
  firstFileUrl: string;
  display_name: string;
  name: string;
  price_total: number;
  price_total_net: number;
  price_regular: number;
  price_recular_net: number;
  adjustment_amount: number;
  adjustment_amount_net: number;
  unit_shortname: string;
  productCategories: ProductCategory[];
  inStock: boolean;
}

type Order = {
  order_deliverymethods?: any;
  id: number | null;
  order_nr?: string | null;
  uuid: string | null;
  delivery_time: string | null;
  order_delivery_methods?: any;
  order_products?: any;
  shoppingCartExpire?: string | null;
  autoCleanExpiredCart?: boolean;
};

type Person = {
  type?: string;
  id: number;
  firstname: string;
  surname: string;
  phone: string;
  email: string;
  language: string;
  country_code: string;
  organization_country_code: string;
  organization_name?: string;
  organization_reg_nr?: string;
  organization_KMKR_number?: string;
  organization_phone?: string;
  organization_email?: string;
  organization_address?: string;
  organization_historical_names?: string;
  organization_ariregister_status?: string;
  organization_ariregister_url?: string;
};

export type PaymentMethod = {
  id: number;
  name: string;
  value: string;
  sort_order: number;
  state: number;
  is_autoset_activated?: boolean;
  is_set_by_system?: boolean;
  description: string;
  image: string;
  api_client_id: number;
  locale_translations: any;
  getMaksekeskusApiActive: {
    id: number;
    name: string;
    pivot: {
      payment_method_id: number;
      attribute_id: number;
      value: string;
    };
  };
  imageUrl: string;
  pivot: {
    person_id: number;
    payment_method_id: number;
    location_id: number;
  };
  api_client: {
    id: 2;
    type: string;
    status: string;
    name: null | string;
    comment: string;
    url: string;
    public_key: string;
    secret: string;
    client_id: string;
    environment: string;
    last_connection: string;
    created_at: string;
    updated_at: string;
    deleted_at: null | string;
    maksekeskus_payment_methods: Array<{
      id: number;
      payment_method_id: number | null;
      api_client_id: number;
      name: string;
      url: string;
      country: string;
      logo_url: string;
      type: string;
      created_at: string;
      updated_at: string;
      deleted_at: null | string;
    }>;
  };
};

interface DeliveryMethodPrice {
  id: number;
  delivery_method_id: number;
  name: string;
  price: number;
  price_neto: string;
  vat: number;
  weight_min: number;
  weight_max: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
  deleted_at: null | string;
}

export type DeliveryMethod = {
  id: number;
  name: string;
  delivery_method_timings?: any;
  description: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
  deleted_at: null | string;
  type: string;
  deliveryMethodPrice: DeliveryMethodPrice;
  isParcelMachine: boolean;
  getDeliveryLocationsUrl: string;
  locale_translations: any;
  timing_required: number;
};

type OrderDeliveryMethod = {
  id: number;
  order_id: number;
  delivery_method_id: number;
  delivery_method_price_id: number;
  address: string;
  city: string;
  postal_code: string;
  parcelMachineName: string | null;
  parcelDescription: string;
  buyer_is_receiver: number;
  receiverFirstname: string;
  receiverSurname: string;
  receiverEmail: string;
  receiverPhone: string;
  price_neto: number;
  price: number;
  vat: string;
  comment: string | null;
  delivery_method_name: string;
  delivery_method_type: string;
  delivery_method_description: string;
  delivery_method_price_name: string;
  weight: number;
  weight_min: number;
  weight_max: number;
  estimated_delivery_at: string;
  requested_delivery_at?: string;
  delivered_at: null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
};

type ShoppingCartContext = {
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  getItemQuantity: (id: number) => number;
  increaseCartQuantity: (
    id: number,
    variationValues?: any,
    eventId?: number,
    start?: string,
    skipOpenCart?: boolean
  ) => void;
  decreaseCartQuantity: (id: number, eventId?: number) => void;
  setCartQuantity: (
    id: number,
    quantity: number,
    eventId?: number,
    start?: string
  ) => void;
  removeFromCart: (id: number, eventId?: number) => void;
  getOrder: (buyerData?: Person) => Promise<void>;
  setCartExpire: (value: Date) => void;
  cartQuantity: number;
  setCartExpireDate: () => void;
  totalCartSum: number;
  showCartExpire: boolean;
  showCheckoutErrorModal: boolean;
  setShowCheckoutErrorModal: (value: React.SetStateAction<boolean>) => void;
  handleCheckoutErrorModalClose: () => void;
  additionalInfo: string;
  setAdditionalInfo: (value: string) => void;
  setShowCartExpire: (value: React.SetStateAction<boolean>) => void;
  totalCartDiscount: number;
  setTotalCartDiscount: (value: number) => void;
  totalCartSumConfirmed: number;
  productsLoaded: boolean;
  totalCartDiscountConfirmed: number;
  deliveryMehtodConfirmed: OrderDeliveryMethod | null;
  cartItems: CartItem[];
  order: Order;
  buyer: Person;
  STORAGE_EXP_LENGTH: number;
  setBuyer: (value: React.SetStateAction<Person>) => void;
  paymentMethods: PaymentMethod[];
  orderPaymentMethod: PaymentMethod | null;
  setOrderPaymentMethod: (
    value: React.SetStateAction<PaymentMethod | null>
  ) => void;
  deliveryMethods: DeliveryMethod[];
  confirmDeliveryData: (data: Object, onSuccess?: () => void) => void;
  orderDeliveryMethod: OrderDeliveryMethod | null;
  setOrderDeliveryMethod: (
    value: React.SetStateAction<OrderDeliveryMethod | null>
  ) => void;
  locationProducts: LocationProduct[];
  setLocationProducts: (value: React.SetStateAction<LocationProduct[]>) => void;
  checkoutConfirm: (onSuccess?: () => void) => void;
  clearData: () => void;
  setConfirmedOrder: (value: React.SetStateAction<Order>) => void;
  confirmedOrder: Order;
  cartItemsConfirmed: CartItem[];
  timeRemaining?: number;
  cartExpire: Date;
  getMaxAvailable: (productId: number, eventId?: number) => number | null;
  hasExceededMaxAvailable: () => boolean;
};

const ShoppingCartContext = createContext({} as ShoppingCartContext);

export function useShoppingCart() {
  return useContext(ShoppingCartContext);
}
export function ShoppingCartProvider({ children }: ShoppingCartProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [deliveryMethods, setDeliveryMethods] = useState<DeliveryMethod[]>([]);
  const [orderDeliveryMethod, setOrderDeliveryMethod] =
    useState<OrderDeliveryMethod | null>(Object);
  const [orderPaymentMethod, setOrderPaymentMethod] =
    useState<PaymentMethod | null>(Object);

  const [showCartExpire, setShowCartExpire] = useState(false);
  const [showCheckoutErrorModal, setShowCheckoutErrorModal] = useState(false);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState<Order>(Object);
  const [cartItemsConfirmed, setCartItemsConfirmed] = useState<CartItem[]>([]);
  const [totalCartSumConfirmed, setTotalCartSumConfirmed] = useState(0);
  const [totalCartDiscountConfirmed, setTotalCartDiscountConfirmed] =
    useState(0);
  const [deliveryMehtodConfirmed, setDeliveryMehtodConfirmed] =
    useState<OrderDeliveryMethod | null>(Object);
  // Cart expiry timeout (use ref to avoid async state races)
  const cartExpiryTimerRef = React.useRef<number | null>(null);

  const STORAGE_EXP_LENGTH = 10800000;
  const [cartExpire, setCartExpire] = useLocalStorage<Date>(
    "shopping-cart-expire",
    new Date(Date.now() + STORAGE_EXP_LENGTH)
  );
  const [cartItems, setCartItems] = useLocalStorage<CartItem[]>(
    "shopping-cart",
    []
  );
  const [buyer, setBuyer] = useLocalStorage<Person>("buyer", Object);
  const [locationProducts, setLocationProducts] = useLocalStorage<
    LocationProduct[]
  >("locationProducts", []);
  const [order, setOrder] = useLocalStorage<Order>("order", Object);
  const [additionalInfo, setAdditionalInfo] = useState("");

  const [totalCartSum, setTotalCartSum] = useState(
    cartItems.reduce((total, cartItem) => {
      const item = locationProducts.find((i) => i.id === cartItem.id);
      return total + (item?.price_total || 0) * cartItem.quantity;
    }, 0)
  );
  const [totalCartDiscount, setTotalCartDiscount] = useState(
    cartItems.reduce((total, cartItem) => {
      const item = locationProducts.find((i) => i.id === cartItem.id);

      return (
        (total +
          (item && item.adjustment_amount ? item?.adjustment_amount : 0)) *
        cartItem.quantity
      );
    }, 0)
  );

  useEffect(() => {
    if (typeof API !== "undefined") {
      window
        // @ts-ignore
        .axios("/loadLocationProducts.json", {
          method: "GET",
          cache: "no-cache",
          headers: { "Content-Type": "application/json" },
        })
        .then((res: any) => {
          setLocationProducts((currItems) => {
            const newItems = [...res.data];
            return newItems;
          });
        })
        .then(() => setProductsLoaded(true));
    } else {
      fetch("/webstoreApi/loadLocationProducts.json", {
        method: "GET",
        cache: "no-cache",
        headers: { "Content-Type": "application/json" },
      })
        .then((res) => res.json())
        .then((res) => {
          setLocationProducts((currItems) => {
            const newItems = [...res];
            return newItems;
          });
        })
        .then(() => setProductsLoaded(true));
    }
  }, []);

  useEffect(() => {
    setTotalCartSum(
      cartItems.reduce((total, cartItem) => {
        const item = locationProducts.find((i) => i.id === cartItem.id);
        return total + (item?.price_total || 0) * cartItem.quantity;
      }, 0)
    );

    setTotalCartDiscount(
      cartItems.reduce((total, cartItem) => {
        const item = locationProducts.find((i) => i.id === cartItem.id);

        return (
          (total +
            (item && item.adjustment_amount ? item?.adjustment_amount : 0)) *
          cartItem.quantity
        );
      }, 0)
    );
  }, [cartItems]);

  // Set up cart expiry timer on mount and when order/cartExpire changes
  useEffect(() => {
    // Always clear existing timers first to prevent duplicates
    clearCartExpiryTimer();

    if (cartItems.length > 0) {
      // Ensure cartExpire is a Date object (in case useLocalStorage returns string)
      const currentExpiry =
        cartExpire instanceof Date ? cartExpire : new Date(cartExpire);
      const autoClean = order?.autoCleanExpiredCart === true;

      // Check if cart is already expired
      if (currentExpiry <= new Date()) {
        handleCartExpiry(autoClean);
      } else {
        setupCartExpiryTimer(currentExpiry, autoClean);
      }
    } else {
      // No cart items
    }

    // Cleanup timers when component unmounts
    return () => {
      clearCartExpiryTimer();
      clearCountdownTimer();
    };
  }, [cartExpire, order?.autoCleanExpiredCart, cartItems.length]);

  // Cart expiry is now handled by the reactive timer system
  // Legacy check removed to prevent conflicts

  function clearData() {
    clearCartExpiryTimer(); // Clear any active timers
    setCartExpireDate();
    setCartItems([]);
    setBuyer(Object);
    // setLocationProducts([])
    setOrder(Object);
    localStorage.setItem("order", JSON.stringify({}));
    setOrderPaymentMethod(null);
    setOrderDeliveryMethod(null);
    setDeliveryMethods([]);
    setPaymentMethods([]);
  }

  // Helper function to check if backend controls expiry
  function isBackendControlledExpiry(): boolean {
    return !!order?.shoppingCartExpire;
  }

  // Clear any existing expiry timeout
  function clearCartExpiryTimer() {
    if (cartExpiryTimerRef.current) {
      window.clearTimeout(cartExpiryTimerRef.current);
      cartExpiryTimerRef.current = null;
    }
  }

  // Countdown is handled locally in UI; no-op here to keep compatibility
  function clearCountdownTimer() {}

  // Countdown display is handled in CartExpiryCountdown component now
  function startCountdownDisplay(expiryDate: Date) {}

  // Set up reactive timer for cart expiry
  function setupCartExpiryTimer(expiryDate: Date, autoClean: boolean = false) {
    // Clear any existing timers to prevent duplicates
    clearCartExpiryTimer();
    clearCountdownTimer();

    const now = new Date();
    const expiry =
      expiryDate instanceof Date ? expiryDate : new Date(expiryDate);
    const timeUntilExpiry = expiry.getTime() - now.getTime();

    if (timeUntilExpiry <= 0) {
      // Already expired
      handleCartExpiry(autoClean);
      return;
    }

    const timer = window.setTimeout(() => {
      // Use a function to get current cart items instead of closure
      const currentCartItems = JSON.parse(
        localStorage.getItem("shopping-cart") || "[]"
      );

      if (currentCartItems.length === 0) {
        return;
      }

      if (autoClean) {
        clearData();
      } else {
        setShowCartExpire(true);
      }
    }, timeUntilExpiry);

    cartExpiryTimerRef.current = timer;

    // Visual countdown handled in UI component
  }

  // Handle cart expiry based on auto-clean setting
  function handleCartExpiry(autoClean: boolean = false) {
    if (cartItems.length === 0) {
      return;
    }

    if (autoClean) {
      clearData();
    } else {
      setShowCartExpire(true);
    }
  }

  function setCartExpireDate() {
    // Only extend locally if backend doesn't control expiry
    if (!isBackendControlledExpiry()) {
      const newExpiry = new Date(Date.now() + STORAGE_EXP_LENGTH);
      setCartExpire(newExpiry);

      // Clear existing timers before setting up new ones
      clearCartExpiryTimer();
      // Set up timer for local expiry (always shows modal)
      setupCartExpiryTimer(newExpiry, false);
    } else {
      // Backend controls expiry, not extending locally
    }
  }

  function setCartExpireDateFromBackend(
    backendExpireDate?: string,
    autoCleanExpiredCart?: boolean
  ) {
    // Clear any existing timers first to prevent duplicates
    clearCartExpiryTimer();

    // Handle all combinations of backend values
    if (backendExpireDate) {
      // Case 1: shoppingCartExpire is present
      const backendDate = new Date(backendExpireDate);

      if (!isNaN(backendDate.getTime())) {
        if (backendDate > new Date()) {
          // Valid future date - set backend expiry
          setCartExpire(backendDate);

          // Set up timer with auto-clean setting
          const shouldAutoClean = autoCleanExpiredCart === true;
          setupCartExpiryTimer(backendDate, shouldAutoClean);

          return true;
        } else {
          // Backend expiry is past
          if (autoCleanExpiredCart === true && cartItems.length > 0) {
            clearData();

            return true;
          } else {
            setShowCartExpire(true);

            return false;
          }
        }
      } else {
        // Invalid backend date format – ignore and fallback below
      }
    } else if (autoCleanExpiredCart === true) {
      // Case 2: No shoppingCartExpire but autoCleanExpiredCart is true
      // Use local expiry with auto-clean behavior
      const localExpiry = new Date(Date.now() + STORAGE_EXP_LENGTH);
      setCartExpire(localExpiry);
      setupCartExpiryTimer(localExpiry, true);
      return true;
    }

    // Case 3: Neither present or invalid - fallback to local expiry.
    // Important: if local expiry is already in the past (common after returning to an expired cart),
    // reset it so the user can continue seamlessly after backend renews the order.
    const currentExpiry =
      cartExpire instanceof Date ? cartExpire : new Date(cartExpire);
    const now = new Date();
    const fallbackExpiry =
      currentExpiry <= now
        ? new Date(Date.now() + STORAGE_EXP_LENGTH)
        : currentExpiry;

    if (fallbackExpiry !== currentExpiry) {
      setCartExpire(fallbackExpiry);
    }
    setupCartExpiryTimer(fallbackExpiry, false);
    return false;
  }

  function handleDataLayerCheckoutConfirm(orderNr: string) {
    window.dataLayer = window.dataLayer || [];
    const deliveryPrice =
      orderDeliveryMethod !== null && orderDeliveryMethod?.price
        ? Number(orderDeliveryMethod.price)
        : 0;

    const totalPrice = pf(
      cartItems.reduce((total, cartItem) => {
        const item = locationProducts.find((i) => i.id === cartItem.id);
        return total + (item?.price_total || 0) * cartItem.quantity;
      }, 0)
    );

    const locationProductItems = cartItems.map((item) => {
      const locationProduct = locationProducts.find((i) => i.id === item.id);
      const locationProductName = {};
      let categories: { category_name: { [k: string]: string } }[] = [];

      if (locationProduct) {
        // Format locationProductName
        Object.entries(locationProduct.translations).map((translation) => {
          const value = translation[1] as { display_name: string };
          locationProductName[translation[0]] = {
            name: value.display_name,
          };
        });

        // Format categories
        locationProduct.productCategories.map((category) => {
          const formattedCategory = {
            category_name: Object.fromEntries(
              Object.entries(category.locale_translations).map(
                ([locale, translation]) => [
                  locale,
                  translation as unknown as string,
                ]
              )
            ),
          };

          // Add the formatted category to the categories array
          categories.push(formattedCategory);
        });
      }

      if (!locationProduct) {
        return null;
      }

      return {
        item_code: locationProduct?.id,
        item_name: locationProductName,
        categories: categories,
        price_regular: locationProduct.price_regular,
        discount_amount: Number(
          locationProduct.price_regular - locationProduct.price_total
        ),
        quantity: 1,
      };
    });

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "purchase",
      ecommerce: {
        total_amount: pf(
          cartItems.reduce((total, cartItem) => {
            const item = locationProducts.find((i) => i.id === cartItem.id);
            return total + (item?.price_total || 0) * cartItem.quantity;
          }, 0) -
            totalCartDiscount +
            (orderDeliveryMethod ? deliveryPrice : 0)
        ),
        total_amount_net: pf(
          cartItems.reduce((total, cartItem) => {
            const item = locationProducts.find((i) => i.id === cartItem.id);
            return total + (item?.price_total_net || 0) * cartItem.quantity;
          }, 0) -
            totalCartDiscount +
            (orderDeliveryMethod
              ? orderDeliveryMethod
                ? orderDeliveryMethod &&
                  orderDeliveryMethod.price_neto &&
                  cartItems.length > 0
                  ? Number(orderDeliveryMethod?.price_neto)
                  : 0
                : 0
              : 0)
        ),
        products_amount: pf(
          cartItems.reduce((total, cartItem) => {
            const item = locationProducts.find((i) => i.id === cartItem.id);
            return total + (item?.price_regular || 0) * cartItem.quantity;
          }, 0) - totalCartDiscount
        ),
        products_amount_net: pf(
          cartItems.reduce((total, cartItem) => {
            const item = locationProducts.find((i) => i.id === cartItem.id);
            return total + (item?.price_recular_net || 0) * cartItem.quantity;
          }, 0) - totalCartDiscount
        ),
        shipping_amount: orderDeliveryMethod ? deliveryPrice : 0,
        shipping_amount_net: orderDeliveryMethod
          ? orderDeliveryMethod &&
            orderDeliveryMethod.price_neto &&
            cartItems.length > 0
            ? Number(orderDeliveryMethod?.price_neto)
            : 0
          : 0,

        order_id: orderNr,
        items: [...locationProductItems],
      },
    });

    // window.dataLayer.push({
    //   event: "purchase",
    //   ecommerce: {
    //     currency: "Eur",
    //     value: totalPrice, //  Set value to the sum of (price * quantity) for all items in items. Don't include shipping or tax.
    //     transaction_id: order.order_nr, //tellimuse number
    //     coupon: "",
    //     shipping: deliveryPrice, // kui on tarne hind, siis tema hind
    //     tax: 0, // käibemaks
    //     items: cartItems.map((item) => {
    //       const locationProduct = locationProducts.find(
    //         (i) => i.id === item.id
    //       );

    //       return {
    //         item_id: locationProduct?.id,
    //         item_name: locationProduct?.product.name,
    //         cupon: locationProduct?.adjustments[0]?.display_name,
    //         discount: locationProduct?.price_with_discounts,
    //         price: locationProduct?.price_total,
    //         quantity: item.quantity,
    //       };
    //     }),
    //   },
    // });
  }

  function checkoutConfirm(onSuccess?: () => void) {
    // @ts-ignore
    window.axios
      .post("checkoutConfirm", {
        order: order,
        orderPaymentMethod: orderPaymentMethod,
        buyer: buyer,
        checkout_url: window.location.href,
        note: additionalInfo,
      })
      .then((res: any) => {
        if (res.data.maksekeskusRedirect) {
          window.location.href = res.data.maksekeskusRedirect;
        } else {
          handleDataLayerCheckoutConfirm(res.data.order.order_nr);
          setConfirmedOrder(res.data.order);
          setCartItemsConfirmed(res.data.order.order_products);
          setTotalCartSumConfirmed(res.data.order.total_amount);
          setTotalCartDiscountConfirmed(res.data.order.total_discount);
          setDeliveryMehtodConfirmed(orderDeliveryMethod);

          setCartItems([]);

          setOrder({ id: null, uuid: null, delivery_time: null });

          // Find page with page type of "order_confirmed"
        }
        if (onSuccess) {
          onSuccess();
        }
      })
      .catch((error: any) => {
        console.log(error);
        setShowCheckoutErrorModal(true);
      });
  }

  function handleCheckoutErrorModalClose() {
    setShowCheckoutErrorModal(false);
    getOrder();
  }

  function confirmDeliveryData(data: Object, onSuccess?: () => void) {
    // @ts-ignore
    window.axios
      .post("confirmDeliveryData", {
        ...data,
        order: order,
        buyer: buyer,
      })
      .then((res: any) => {
        if (res.data.order) {
          setOrder(res.data.order);
          // Handle cart expiry from backend
          setCartExpireDateFromBackend(
            res.data.order.shoppingCartExpire,
            res.data.order.autoCleanExpiredCart
          );
        } else {
          setOrder(Object);
          // If no order returned, fallback to local expiry
          setCartExpireDate();
        }
        setOrderDeliveryMethod(res.data.orderDeliveryMethod);
        if (onSuccess) {
          onSuccess();
        }
      })
      .catch((error: any) => {
        console.log(error);
        // On error, fallback to local expiry
        setCartExpireDate();
      });
  }

  function getOrder(buyerData?: Person): Promise<void> {
    // @ts-ignore
    return window.axios
      .post("getOrder", {
        cartItems: cartItems,
        order: order,
        buyer: buyerData ? buyerData : buyer,
      })
      .then((res: any) => {
        if (res.data.order) {
          setOrder(res.data.order);

          // Backend order received – update order and timers accordingly

          // Handle cart expiry from backend
          setCartExpireDateFromBackend(
            res.data.order.shoppingCartExpire,
            res.data.order.autoCleanExpiredCart
          );

          let cartHasItemsThatNoLongerExist = false;
          cartItems.map((item) => {
            // Find the matching order product by both location_product_id and event_id
            const matchingOrderProduct = res.data.order.order_products.find(
              (order_product: any) =>
                order_product.location_product_id === item.id &&
                (item.eventId ? order_product.event_id === item.eventId : true)
            );

            if (matchingOrderProduct && matchingOrderProduct.event?.id) {
              // Set the event start date from the matched order product
              item.start = matchingOrderProduct.event.start;
            }

            // Check if the cart item exists in order products (for cleanup)
            if (!matchingOrderProduct) {
              cartHasItemsThatNoLongerExist = true;
            }
          });

          // cartItems state used for cleanup below

          if (cartHasItemsThatNoLongerExist) {
            setCartItems((currItems) => {
              return currItems.filter((item) => {
                return res.data.order.order_products.find(
                  (order_product: any) =>
                    order_product.location_product_id === item.id &&
                    (item.eventId
                      ? order_product.event_id === item.eventId
                      : true)
                );
              });
            });
          }
        }

        if (res.data.paymentMethods) {
          setPaymentMethods(res.data.paymentMethods);
        }

        if (res.data.deliveryMethods) {
          setDeliveryMethods(res.data.deliveryMethods);
        }
        if (res.data.orderDeliveryMethod) {
          setOrderDeliveryMethod(res.data.orderDeliveryMethod);
        }
        if (res.data.locationProducts) {
          setLocationProducts((currItems) => {
            let newItems = [...currItems];
            res.data.locationProducts.map(
              (locationProduct: LocationProduct) => {
                if (
                  currItems.find((item) => item.id === locationProduct.id) ==
                  null
                ) {
                  newItems = [...newItems, locationProduct];
                } else {
                  newItems = newItems.map((item) => {
                    if (item.id === locationProduct.id) {
                      return { ...item, ...locationProduct };
                    } else {
                      return item;
                    }
                  });
                }
              }
            );
            return newItems;
          });
        }

        if (res.data.orderCompleted && res.data.order.finished_at === null) {
          setConfirmedOrder(Object);
          setCartItemsConfirmed([]);
          setCartItems([]);
          setOrder({ id: null, uuid: null, delivery_time: null });

          return;
        }

        if (res.data.orderCompleted) {
          setConfirmedOrder(res.data.order);
          setCartItemsConfirmed(res.data.order.order_products);
          if (res.data.orderPaymentMethod) {
            setOrderPaymentMethod(res.data.orderPaymentMethod);
          }

          setCartItems([]);
          setOrder({ id: null, uuid: null, delivery_time: null });
        }

        // If no order was returned from backend, fallback to local expiry
        if (!res.data.order) {
          console.log(
            "No order returned from backend, using local cart expiry"
          );
          setCartExpireDate();
        }
      })
      .catch((error: any) => {
        console.log(error);
        // On error, fallback to local expiry to ensure cart doesn't stay expired indefinitely
        setCartExpireDate();
      });
  }

  const cartQuantity = cartItems.reduce(
    (quantity, item) => item.quantity + quantity,
    0
  );

  const openCart = () => setIsOpen(true);
  const closeCart = () => setIsOpen(false);
  function getItemQuantity(id: number) {
    return cartItems.find((item) => item.id === id)?.quantity || 0;
  }

  function handleDataLayerAddToCart(id: any) {
    const locationProduct = locationProducts.find((item) => item.id === id);

    if (locationProduct) {
      const locationProductName: { [key: string]: { name: string } } = {};
      let categories: { category_name: { [k: string]: string } }[] = [];

      // Format locationProductName
      Object.entries(locationProduct.translations).map((translation) => {
        const value = translation[1] as { display_name: string };
        locationProductName[translation[0]] = {
          name: value.display_name,
        };
      });

      // Format categories
      locationProduct.productCategories.map((category) => {
        const formattedCategory = {
          category_name: Object.fromEntries(
            Object.entries(category.locale_translations).map(
              ([locale, translation]) => [
                locale,
                translation as unknown as string,
              ]
            )
          ),
        };

        // Add the formatted category to the categories array
        categories.push(formattedCategory);
      });

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "add_to_cart",
        ecommerce: {
          products_amount: Number(locationProduct.price_total)
            ? Number(locationProduct.price_total)
            : Number(locationProduct.price_total),

          products_amount_net: Number(locationProduct.price_total_net)
            ? Number(locationProduct.price_total_net)
            : Number(locationProduct.price_total_net),

          items: [
            {
              item_code: locationProduct.id,
              item_name: locationProductName,
              categories: categories,
              price_regular: locationProduct.price_regular,
              discount_amount: Number(
                locationProduct.price_regular - locationProduct.price_total
              ),
              quantity: 1,
            },
          ],
        },
      });
    }
  }

  function toggleCart() {
    setIsOpen((curr) => !curr);
  }
  function increaseCartQuantity(
    id: number,
    variationValues?: any,
    eventId?: number,
    start?: string,
    skipOpenCart?: boolean
  ) {
    // Only extend expiry if backend doesn't control it
    setCartExpireDate();
    setCartItems((currItems) => {
      // When adding an item, check both id and eventId (if eventId is provided)
      const existingItemIndex = currItems.findIndex(
        (item) => item.id === id && (eventId ? item.eventId === eventId : true)
      );

      if (existingItemIndex === -1) {
        return [
          ...currItems,
          {
            id,
            quantity: 1,
            addVariationProductProductSelectedVariationData: variationValues,
            eventId,
            start,
          },
        ];
      } else {
        return currItems.map((item, index) => {
          if (index === existingItemIndex) {
            return {
              ...item,
              quantity: item.quantity + 1,
              addVariationProductProductSelectedVariationData: variationValues,
              eventId,
              start,
            };
          } else {
            return item;
          }
        });
      }
    });
    if (!skipOpenCart) {
      openCart();
    }

    handleDataLayerAddToCart(id);
  }
  function decreaseCartQuantity(id: number, eventId?: number) {
    // Only extend expiry if backend doesn't control it
    setCartExpireDate();
    setCartItems((currItems) => {
      const existingItemIndex = currItems.findIndex(
        (item) => item.id === id && (eventId ? item.eventId === eventId : true)
      );

      if (existingItemIndex === -1) return currItems;

      const existingItem = currItems[existingItemIndex];

      if (existingItem.quantity === 1) {
        return currItems;
        // return currItems.filter(item => item.id !== id)
      } else {
        return currItems.map((item, index) => {
          if (index === existingItemIndex) {
            return { ...item, quantity: item.quantity - 1 };
          } else {
            return item;
          }
        });
      }
    });
  }
  function setCartQuantity(id: number, quantity: number, eventId?: number) {
    // Only extend expiry if backend doesn't control it
    setCartExpireDate();
    setCartItems((currItems) => {
      if (quantity <= 0) {
        return currItems.filter(
          (item) =>
            !(item.id === id && (eventId ? item.eventId === eventId : true))
        );
      } else {
        return currItems.map((item) => {
          if (item.id === id && (eventId ? item.eventId === eventId : true)) {
            return { ...item, quantity: quantity };
          } else {
            return item;
          }
        });
      }
    });
  }
  function removeFromCart(id: number, eventId?: number) {
    // Only extend expiry if backend doesn't control it
    setCartExpireDate();
    setCartItems((currItems) => {
      return currItems.filter(
        (item) =>
          !(item.id === id && (eventId ? item.eventId === eventId : true))
      );
    });
  }

  // Get maxAvailable for a specific product, optionally filtered by eventId
  function getMaxAvailable(productId: number, eventId?: number): number | null {
    if (
      !order ||
      !order.order_products ||
      !Array.isArray(order.order_products)
    ) {
      return null;
    }

    // Find the matching order product
    const orderProduct = order.order_products.find((op: any) => {
      if (eventId) {
        // If eventId is provided, match both location_product_id and event_id
        return op.location_product_id === productId && op.event_id === eventId;
      }
      // If no eventId, just match location_product_id
      return op.location_product_id === productId;
    });

    if (orderProduct && typeof orderProduct.maxAvailable === "number") {
      return orderProduct.maxAvailable;
    }

    return null;
  }

  // Check if any cart items exceed their maxAvailable (for events)
  function hasExceededMaxAvailable(): boolean {
    return cartItems.some((cartItem) => {
      // Only check items that have eventId
      if (!cartItem.eventId) return false;

      const maxAvailable = getMaxAvailable(cartItem.id, cartItem.eventId);
      return maxAvailable !== null && cartItem.quantity > maxAvailable;
    });
  }

  return (
    <ShoppingCartContext.Provider
      value={{
        setShowCartExpire,
        productsLoaded,
        setCartExpire,
        getItemQuantity,
        showCartExpire,
        showCheckoutErrorModal,
        setShowCheckoutErrorModal,
        handleCheckoutErrorModalClose,
        setCartExpireDate,
        increaseCartQuantity,
        decreaseCartQuantity,
        setCartQuantity,
        removeFromCart,
        openCart,
        closeCart,
        toggleCart,
        cartItems,
        STORAGE_EXP_LENGTH,
        cartQuantity,
        additionalInfo,
        setAdditionalInfo,
        totalCartSum,
        totalCartDiscount,
        setTotalCartDiscount,
        deliveryMehtodConfirmed,
        totalCartDiscountConfirmed,
        totalCartSumConfirmed,
        locationProducts,
        setLocationProducts,
        clearData,
        buyer,
        setBuyer,
        order,
        getOrder,
        getMaxAvailable,
        hasExceededMaxAvailable,
        deliveryMethods,
        orderDeliveryMethod,
        setOrderDeliveryMethod,
        confirmDeliveryData,
        paymentMethods,
        orderPaymentMethod,
        setOrderPaymentMethod,
        checkoutConfirm,
        setConfirmedOrder,
        confirmedOrder,
        cartItemsConfirmed,
        timeRemaining: 0,
        cartExpire,
      }}
    >
      {children}
      <ShoppingCart isOpen={isOpen} />
    </ShoppingCartContext.Provider>
  );
}
